import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import moment from 'moment';
import fetch from 'node-fetch';
import { formatLocDateMMDDYY } from '../formatters';
import { AideRoleGroupId, NurseRoleGroupId, SocialWorkerRoleGroupId, SpiritualRoleGroupId, VolunteerRoleGroupId } from '../roleData';
import { getHeaders } from '../utils';
import { AS, AsNeeded, Assess, BIMO, BIWK, BiMonthly, BiWeekly, DIS, Daily, Discontinued, MO, Monthly, NotAssigned, OneXAssess, Refused, ZeroFreq, TwentyFourHours, WK, Weekly, rgProps } from './frequency-constants';
import { formatSchedulerTimestamp } from './frequency-formatters';
import { hasPrimaryEffectiveDate } from './frequency-types';
// util for sorting roles by order specified in constant file
export const sortRoles = (roleGroup1, roleGroup2) => {
  if (roleGroup1.order > roleGroup2.order) {
    return 1;
  }

  return -1;
}; // eg 01/28/2004 gets converted to 20040829

export const monthToComparableNumber = date => {
  if (date === undefined || date === null || date.length !== 8) {
    return null;
  }

  const yearNumber = parseInt(`20${date.substring(6, 8)}`);
  const dayNumber = parseInt(date.substring(3, 5));
  const monthNumber = parseInt(date.substring(0, 2));
  const result = yearNumber * 10000 + monthNumber * 100 + dayNumber;
  return result;
};
/** for sorting effective date */

export const dateComparator = (plan1, plan2) => {
  let date1Number;
  let date2Number;

  if (plan1.effectiveDate.length > 8) {
    date1Number = monthToComparableNumber(plan1.effectiveDate.slice(0, 8));
  } else {
    date1Number = monthToComparableNumber(plan1.effectiveDate);
  }

  if (plan2.effectiveDate.length > 8) {
    date2Number = monthToComparableNumber(plan2.effectiveDate.slice(0, 8));
  } else {
    date2Number = monthToComparableNumber(plan2.effectiveDate);
  }

  if (date1Number === null && date2Number === null) {
    return 0;
  }

  if (date1Number === null) {
    return -1;
  }

  if (date2Number === null) {
    return 1;
  }

  return date2Number - date1Number;
};
/** show period */

export const showFrequency = statePeriod => statePeriod ? statePeriod === Weekly || statePeriod === Monthly : false;
/** set period to 1 */

export const setFrequencyAtOne = statePeriod => statePeriod ? statePeriod === BiWeekly || statePeriod === BiMonthly || statePeriod === AsNeeded || statePeriod === Daily || statePeriod === TwentyFourHours || statePeriod === OneXAssess : false;
/** set period to 0 */

export const setFrequencyAtZero = statePeriod => statePeriod ? statePeriod === Refused || statePeriod === ZeroFreq || statePeriod === Discontinued : false;
/** Prioritizes dischargeDate over nextIdg date. If neither exist return undefined. */

export const findMaxEditableDate = (patient, idg, includeTwoWeeks) => {
  let dischargeDate = '';
  if (patient !== null && patient !== void 0 && patient.admittedDate) dischargeDate = formatLocDateMMDDYY(patient.dischargedDate);
  let nextIdg = '';
  if (idg !== null && idg !== void 0 && idg.agendastartplusone) nextIdg = formatLocDateMMDDYY(idg.agendastop);
  const twoWeeksFromNow = includeTwoWeeks && moment().add(14, 'd').format('MM/DD/YY') || moment().add(1, 'y').format('MM/DD/YY');
  const filteredDates = [dischargeDate, nextIdg, twoWeeksFromNow].filter(date => date !== '');
  const sortedDates = filteredDates.sort((a, b) => Date.parse(a) > Date.parse(b) ? -1 : 1);
  return moment(new Date(sortedDates[0])).format('MM/DD/YY');
};
/** Finds and returns the most recent date between admit, lastIdg, and the current frequency. If none exist return undefined */

export const findMinEditableDate = (basicDateConstraint, effective) => {
  let currentFrequency = null;

  if (effective) {
    currentFrequency = formatLocDateMMDDYY(effective);
  }

  const filteredDates = [basicDateConstraint, currentFrequency].filter(date => !!date);

  if (filteredDates.length) {
    const sortedDates = filteredDates.sort((a, b) => Date.parse(a) > Date.parse(b) ? -1 : 1);
    return moment(new Date(sortedDates[0])).format('MM/DD/YY');
  }

  return null;
};
/** Helper function */

export const abbreviateText = (frequencyText, period, isPN) => {
  switch (period) {
    case BiWeekly:
      return frequencyText.replace(BiWeekly, BIWK);

    case Weekly:
      return frequencyText.replace(Weekly, WK);

    case Monthly:
      return frequencyText.replace(Monthly, MO);

    case BiMonthly:
      return frequencyText.replace(BiMonthly, BIMO);

    case Assess:
      return frequencyText.replace(Assess, AS);

    case Discontinued:
      return isPN ? frequencyText.replace(Discontinued, Discontinued) : frequencyText.replace(Discontinued, DIS);

    default:
      return frequencyText;
  }
}; // For removing un-necessary frequencies from periods.
// '1 X Daily' => 'Daily'

export const truncatePeriod = frequency => {
  var _frequency$split$;

  const splitFreq = frequency === null || frequency === void 0 ? void 0 : (_frequency$split$ = frequency.split('x')[1]) === null || _frequency$split$ === void 0 ? void 0 : _frequency$split$.trim();

  if (splitFreq === BIWK || splitFreq === BiWeekly || splitFreq === BIMO || splitFreq === BiMonthly || splitFreq === Refused || splitFreq === ZeroFreq || splitFreq === AsNeeded || splitFreq === Daily || splitFreq === Assess || splitFreq === Discontinued || splitFreq === DIS) {
    return splitFreq;
  }

  return frequency && frequency.includes('undefined') ? splitFreq : frequency;
};

const getEffectiveDateDisplay = (effectiveDate, requiresDatePresent) => {
  if (requiresDatePresent && !effectiveDate) return '';
  return effectiveDate ? moment(effectiveDate).format('MM/DD/YY') : '';
}; // used to generate non current row information on cpf


export const getNonCurrentCPFInfoFromRow = carePlan => {
  var _carePlan$prn, _carePlan$roleGroupNa, _carePlan$daysOfWeek, _carePlan$workerName;

  return {
    careplan_id: carePlan.id,
    is_current: 0,
    is_future: carePlan.isFuturePlan ? 1 : 0,
    prn: (_carePlan$prn = carePlan.prn) !== null && _carePlan$prn !== void 0 ? _carePlan$prn : false,
    role_group_name: (_carePlan$roleGroupNa = carePlan.roleGroupName) !== null && _carePlan$roleGroupNa !== void 0 ? _carePlan$roleGroupNa : '',
    role_group_id: carePlan.roleGroupId,
    daysOfWeek: (_carePlan$daysOfWeek = carePlan.daysOfWeek) !== null && _carePlan$daysOfWeek !== void 0 ? _carePlan$daysOfWeek : '',
    workerName: (_carePlan$workerName = carePlan.workerName) !== null && _carePlan$workerName !== void 0 ? _carePlan$workerName : '',
    discipline: carePlan.workerName === NotAssigned ? '' : carePlan.discipline,
    frequencyText: truncatePeriod(abbreviateText(carePlan.frequencyText, carePlan.period)),
    effectiveDate: getEffectiveDateDisplay(carePlan.effectiveDate, !!(carePlan.isCurrentPlan || carePlan.isFuturePlan))
  };
};
/**
 * Creating an Async foreach helper function
 * @param {*} array
 * @param {*} callback
 */

export const asyncForEach = async function (array, callback) {
  for (let index = 0; index < (array === null || array === void 0 ? void 0 : array.length); index++) {
    // eslint-disable-next-line no-await-in-loop
    await callback(array[index], index, array);
  }
};
export const clearFrequencies = roleGroup => {
  const {
    frequencies
  } = roleGroup;
  Object.keys(frequencies).forEach(carePlanId => {
    if (carePlanId !== 'dummy') delete frequencies[carePlanId];
  });
};
/**
 * @param: roleGroupList - list of role groups from pimreader
 * @param: idgTeamList - list of idg team members on the patients assigned team
 *
 *  hashes role groups by role id and adds idg team members to rg.
 */

export const hashRoleGroupList = (roleGroupsList, roleList) => {
  roleGroupsList && roleGroupsList.forEach(roleGroup => {
    if (rgProps[roleGroup.id.toString()]) {
      Object.assign(roleGroup, rgProps[roleGroup.id.toString()]);
    } else {
      roleGroup.order = 7;
      roleGroup.defaultRoleGroup = false;
    }
  }); // hash each role group in the list by its id
  // eslint-disable-next-line no-shadow

  const roleGroupsHash = roleGroupsList.reduce((rgHash, roleGroup) => {
    rgHash[roleGroup.id.toString()] = roleGroup;
    return rgHash;
  }, {}); // get the role list object

  const myRoleGroupsHash = Object.fromEntries(Object.entries(roleGroupsHash).map(hashObj => {
    const rg = hashObj[1];

    if (roleList) {
      rg.roles = roleList.filter(role => role.rolegroup_id === rg.id);
    } else {
      rg.roles = [];
    } // dummy plan for  rows with no associated care plan.


    rg.frequencies = {
      dummy: {
        editing: false,
        roleGroupId: rg.id
      }
    };
    rg.pimAssignments = {};
    return [hashObj[0], rg];
  })); // add each team member to the hashed roleGroup by name

  return myRoleGroupsHash;
};

const assignCarePlanTimelineFlags = (carePlan, props, today) => {
  if ((props === null || props === void 0 ? void 0 : props.viewMode) === 'print' && props !== null && props !== void 0 && props.printConfig) {
    // Print view already filtered rows; keep legacy isCurrentPlan semantics.
    carePlan.isCurrentPlan = 1;
    return;
  }

  carePlan.isCurrentPlan = today.isSameOrAfter(carePlan.effectiveDate, 'day') ? 1 : 0;
  carePlan.isFuturePlan = today.isBefore(carePlan.effectiveDate, 'day') ? 1 : 0;
};

const enrichCarePlanRoleGroupName = (carePlan, rgHash) => {
  if (!carePlan.roleGroupName && rgHash) {
    var _rgHash$carePlan$role;

    carePlan.roleGroupName = (_rgHash$carePlan$role = rgHash[carePlan.roleGroupId.toString()]) === null || _rgHash$carePlan$role === void 0 ? void 0 : _rgHash$carePlan$role.name;
  }
};

const appendNonCurrentRow = (slot, carePlan) => {
  var _slot$nonCurrentRows;

  const nonCurrentRows = (_slot$nonCurrentRows = slot.nonCurrentRows) !== null && _slot$nonCurrentRows !== void 0 ? _slot$nonCurrentRows : [];
  slot.nonCurrentRows = [...nonCurrentRows, getNonCurrentCPFInfoFromRow(carePlan)];
};

const setPrimaryCarePlan = (acc, roleGroupId, carePlan, nonCurrentRows = []) => {
  acc[roleGroupId] = _objectSpread(_objectSpread({}, carePlan), {}, {
    nonCurrentRows,
    roleGroupName: carePlan.roleGroupName
  });
};
/** Pick the primary row for a role group and collect older/future rows separately. */


const mergeActiveOrFutureCarePlan = (acc, carePlan) => {
  const {
    roleGroupId,
    roleGroupName,
    effectiveDate,
    isCurrentPlan
  } = carePlan;

  if (!acc[roleGroupId]) {
    acc[roleGroupId] = {
      roleGroupName: roleGroupName !== null && roleGroupName !== void 0 ? roleGroupName : '',
      roleGroupId
    };
  }

  const slot = acc[roleGroupId];

  if (!hasPrimaryEffectiveDate(slot)) {
    var _slot$nonCurrentRows2;

    setPrimaryCarePlan(acc, roleGroupId, carePlan, (_slot$nonCurrentRows2 = slot.nonCurrentRows) !== null && _slot$nonCurrentRows2 !== void 0 ? _slot$nonCurrentRows2 : []);
    return;
  }

  if (isCurrentPlan && moment(effectiveDate).isAfter(moment(slot.effectiveDate))) {
    var _slot$nonCurrentRows3;

    setPrimaryCarePlan(acc, roleGroupId, carePlan, [...((_slot$nonCurrentRows3 = slot.nonCurrentRows) !== null && _slot$nonCurrentRows3 !== void 0 ? _slot$nonCurrentRows3 : []), getNonCurrentCPFInfoFromRow(slot)]);
    return;
  }

  appendNonCurrentRow(slot, carePlan);
};
/** Historical-only role groups still need a primary row for display. */


const mergeHistoricalCarePlan = (acc, carePlan) => {
  acc[carePlan.roleGroupId] = _objectSpread(_objectSpread({}, carePlan), {}, {
    nonCurrentRows: [],
    roleGroupName: carePlan.roleGroupName
  });
};

const buildCarePlansByRoleGroup = (carePlanResponse, rgHash, props) => {
  const tmpCarePlans = {};
  const today = moment();
  (carePlanResponse !== null && carePlanResponse !== void 0 ? carePlanResponse : []).forEach(carePlan => {
    if (!carePlan) {
      return;
    }

    enrichCarePlanRoleGroupName(carePlan, rgHash);
    assignCarePlanTimelineFlags(carePlan, props, today);

    if (carePlan.isCurrentPlan || carePlan.isFuturePlan) {
      mergeActiveOrFutureCarePlan(tmpCarePlans, carePlan);
      return;
    }

    if (!tmpCarePlans[carePlan.roleGroupId]) {
      mergeHistoricalCarePlan(tmpCarePlans, carePlan);
      return;
    }

    appendNonCurrentRow(tmpCarePlans[carePlan.roleGroupId], carePlan);
  });
  return tmpCarePlans;
};

const toProcessedCarePlansByRoleGroup = tmpCarePlans => Object.entries(tmpCarePlans).reduce((processed, [roleGroupId, plan]) => {
  if ('id' in plan && plan.id != null) {
    processed[Number(roleGroupId)] = plan;
  }

  return processed;
}, {});

const injectCarePlansIntoRoleGroupHash = (tmpCarePlans, rgHash) => {
  Object.values(tmpCarePlans).forEach(plan => {
    if (!('id' in plan) || plan.id == null) {
      return;
    }

    const processedPlan = plan;

    if (processedPlan.nonCurrentRows) {
      processedPlan.nonCurrentRows.sort(dateComparator);
    }

    const roleGroup = rgHash[processedPlan.roleGroupId];

    if (roleGroup) {
      const frequencies = roleGroup.frequencies;
      frequencies[processedPlan.id] = processedPlan;
    }
  });
};

export const processRoleGroups = async (carePlanResponse, rgHash, props) => {
  Object.keys(rgHash).forEach(key => clearFrequencies(rgHash[key]));
  const tmpCarePlans = buildCarePlansByRoleGroup(carePlanResponse, rgHash, props);
  injectCarePlansIntoRoleGroupHash(tmpCarePlans, rgHash);
  return [toProcessedCarePlansByRoleGroup(tmpCarePlans), rgHash];
};
export const getUpdatedRgHash = async (carePlanResponse, rgHash, props) => {
  const [, updatedRgHash] = await processRoleGroups(carePlanResponse, rgHash, props);
  return updatedRgHash;
}; // for incorperating obervation PSO values to data structure

export const getRoleGroupPrn = (standingOrder, rgHash) => {
  // if the patient has standing orders, set the appropriate boolean values for role groups
  if (standingOrder && standingOrder[0]) {
    const {
      prnnurse,
      prnspiritual,
      prnsocialworker,
      prnaide,
      prnvol
    } = standingOrder[0]; // Aide

    if (rgHash[AideRoleGroupId.toString()]) rgHash[AideRoleGroupId.toString()].prn = prnaide === 'true'; // Volunteer

    if (rgHash[VolunteerRoleGroupId.toString()]) rgHash[VolunteerRoleGroupId.toString()].prn = prnvol === 'true'; // nurse

    if (rgHash[NurseRoleGroupId.toString()]) rgHash[NurseRoleGroupId.toString()].prn = prnnurse === 'true'; // spiritual

    if (rgHash[SpiritualRoleGroupId.toString()]) rgHash[SpiritualRoleGroupId.toString()].prn = prnspiritual === 'true'; // social work

    if (rgHash[SocialWorkerRoleGroupId.toString()]) rgHash[SocialWorkerRoleGroupId.toString()].prn = prnsocialworker === 'true';
    return rgHash;
  }

  return rgHash;
}; // applies IDs from rgHash to array of objects to be used as ag grid rows

export const filterRgHash = (rgHash, newRowData, orderData) => {
  let mappedHash = Object.values(rgHash).map(roleGroup => {
    const {
      defaultRoleGroup,
      frequencies,
      id,
      name,
      order,
      pimAssignments
    } = roleGroup;
    const freqIds = Object.keys(frequencies).filter(freqId => freqId !== 'dummy');
    const pimProviders = Object.keys(pimAssignments).length;

    if (freqIds.length > 0) {
      return freqIds.map(frequencyId => ({
        id: frequencyId,
        defaultRoleGroup: false,
        roleGroupId: id,
        name,
        order,
        // for rows with multiple assignments from pim.
        rowHeight: pimProviders > 0 ? pimProviders * 26 : 26
      }));
    }

    return {
      id,
      defaultRoleGroup,
      roleGroupId: id,
      name,
      order,
      rowHeight: pimProviders > 0 ? pimProviders * 26 : 26
    };
  });
  mappedHash = mappedHash.flat();
  mappedHash.sort(sortRoles);

  const isOrder = id => {
    const orderList = orderData && Object.values(orderData);
    const isNewRow = newRowData && newRowData.find(row => (row === null || row === void 0 ? void 0 : row.id) === id);
    return !isNewRow && orderList && orderList.find(order => id === (order === null || order === void 0 ? void 0 : order.id));
  };

  mappedHash = mappedHash.filter(plan => plan.defaultRoleGroup || typeof plan.id === 'string' || isOrder(plan.id));

  if (newRowData) {
    newRowData.forEach(data => {
      if (data) {
        const {
          id,
          defaultRoleGroup,
          name,
          order
        } = data;
        const roleGroup = rgHash[id.toString()];
        let newRow = {};

        if (roleGroup) {
          const pimProviders = Object.keys(roleGroup.pimAssignments).length;
          newRow = {
            id,
            defaultRoleGroup,
            roleGroupId: id,
            name,
            order,
            rowHeight: pimProviders > 0 ? pimProviders * 26 : 26
          };
        } else {
          newRow = {
            id,
            defaultRoleGroup,
            roleGroupId: id,
            name,
            order,
            rowHeight: 26
          };
        }

        mappedHash = mappedHash.concat(newRow);
      }
    });
  }

  return mappedHash;
}; // todo call

export const getPatientAssignments = async (rgHash, patientNumber, patientId, agency, keycloak) => {
  let pimAssignments = [];
  const headers = await getHeaders(keycloak);
  headers.set('Authorization', `Bearer ${keycloak === null || keycloak === void 0 ? void 0 : keycloak.token}`);
  headers.set('Accept-Profile', 'pimreader');
  let rolesURL = `${process.env.REACT_APP_AUTH_PROXY_URL}`;
  rolesURL += `/api/query/rpc/getfrequencyproviders?agency=${agency}&p_patientnumber=${patientNumber}&p_patient_id=${patientId}`;
  await fetch(rolesURL, {
    headers,
    credentials: 'same-origin'
  }).then(async data => {
    // A 504 is a Gateway Timeout Error
    if (data.status === 200) {
      pimAssignments = await data.json();
      pimAssignments.forEach(assignment => {
        if (rgHash[assignment.rolegroup_id.toString()]) {
          const selectedRG = rgHash[assignment.rolegroup_id.toString()];
          const assignedProviders = JSON.parse(assignment.providers);
          const hashedProviders = assignedProviders.reduce((seed, provider) => {
            seed[provider.personnumber] = provider;
            return seed;
          }, {});
          selectedRG.pimAssignments = hashedProviders;
        }
      });
    }
  }).catch(error => console.error(`${error}`));
  return pimAssignments;
};
/**
 *  getRoleGroups in CPF and PN
 */

export const getRoleGroups = async (keycloak, agency) => {
  let roleGroupsListFromSource = [];
  const headers = await getHeaders(keycloak);
  headers.set('Authorization', `Bearer ${keycloak === null || keycloak === void 0 ? void 0 : keycloak.token}`);
  headers.set('Accept-Profile', 'pimreader');
  let roleGroupsURL = `${process.env.REACT_APP_AUTH_PROXY_URL}`;
  roleGroupsURL += `/api/query/rolegroupswithroles_vw?agency=${agency}`;
  await fetch(roleGroupsURL, {
    headers,
    credentials: 'same-origin'
  }).then(async data => {
    // A 504 is a Gateway Timeout Error
    if (data.status === 200) {
      roleGroupsListFromSource = await data.json();
    } else {
      roleGroupsListFromSource = [];
    }
  }).catch(error => console.error(`getRoleGroups error: ${error}`));
  return roleGroupsListFromSource;
};
/**
 *  getRoleGroups for dropdown options
 */

export const mapRolesToOptions = roles => {
  if (roles && roles.length) {
    return roles.map(x => _objectSpread(_objectSpread({}, x), {}, {
      label: x.name
    }));
  }

  return [];
};
/**
 *  removes frequency values from frequency text for periods with one associated freq.
 */

export const getFreqText = carePlan => {
  const {
    editPeriod
  } = carePlan;

  switch (editPeriod) {
    case Discontinued:
      return Discontinued;

    case Refused:
      return Refused;

    case ZeroFreq:
      return ZeroFreq;

    case OneXAssess:
      return OneXAssess;

    case BiMonthly:
      return BiMonthly;

    case BiWeekly:
      return BiWeekly;

    case Daily:
      return Daily;

    default:
      return carePlan.editFrequency ? `${carePlan.editFrequency} x ${carePlan.editPeriod}` : `${carePlan.editPeriod}`;
  }
};
/**
 *  For creating a new or updating an existing carePlan by role group id
 */

export const generateNewCarePlanByRoleGroup = (rgHash, carePlan, patient, agency) => {
  const selectedRoleGroup = rgHash[carePlan.roleGroupId.toString()];

  if (selectedRoleGroup) {
    const newEffectiveDate = formatLocDateMMDDYY(carePlan.editEffective);
    const {
      siteId
    } = patient; // TODO: Refactor to boolean flag "discontinued" on careplan table.

    return {
      id: newEffectiveDate === formatLocDateMMDDYY(carePlan.effectiveDate) ? carePlan.id : null,
      patientNumber: patient.patientNumber,
      discipline: selectedRoleGroup.name || '',
      roleGroupName: selectedRoleGroup.name,
      roleGroupId: selectedRoleGroup.id,
      roleName: '',
      roleId: '',
      roleShortName: '',
      nonCurrentRows: [],
      daysOfWeek: carePlan.editDaysOfWeek,
      period: carePlan.editPeriod,
      frequency: carePlan.editFrequency,
      prn: carePlan.editPrn,
      effectiveDate: formatSchedulerTimestamp(carePlan.editEffective),
      endDate: '',
      siteId: (siteId === null || siteId === void 0 ? void 0 : siteId.toString()) || '',
      tenantId: agency || '',
      userComments: '',
      frequencyText: getFreqText(carePlan),
      overwriteFuturePlan: new Date(carePlan.editEffective) > new Date()
    };
  }

  return null;
};
/**
 * generateNewFrequency
 */

export const generateNewFrequency = (rgHash, carePlan, patient, agency) => {
  if (carePlan && patient && agency) {
    const newPlan = generateNewCarePlanByRoleGroup(rgHash, carePlan, patient, agency);
    return newPlan;
  }

  return null;
};
/**
 * getLastIdgInfo
 */

export const getLastIdgInfo = async (keycloak, agency, patientNumber) => {
  const headers = await getHeaders(keycloak);
  headers.set('Authorization', `Bearer ${keycloak === null || keycloak === void 0 ? void 0 : keycloak.token}`);
  headers.set('Accept-Profile', 'pimreader');
  let idgURL = `${process.env.REACT_APP_AUTH_PROXY_URL}`;
  idgURL += `/api/query/idgmeetingmostrecent_vw?agency=${agency}`;
  idgURL += `&patientnumber=eq.${patientNumber}`;
  const patientReviews = await fetch(idgURL, {
    headers,
    credentials: 'same-origin'
  });
  const lastIDGData = // A 504 is a Gateway Timeout Error
  patientReviews.status === 200 ? await patientReviews.json() : [];
  return lastIDGData;
};
/**
 *  getRoleGroups in CPF and PN
 */

export const getRoles = async (keycloak, agency) => {
  let roleGroupsListFromSource = [];
  const headers = await getHeaders(keycloak);
  headers.set('Authorization', `Bearer ${keycloak === null || keycloak === void 0 ? void 0 : keycloak.token}`);
  headers.set('Accept-Profile', 'pimreader');
  let roleGroupsURL = `${process.env.REACT_APP_AUTH_PROXY_URL}`;
  roleGroupsURL += `/api/query/careproviderrole_vw?agency=${agency}`;
  await fetch(roleGroupsURL, {
    headers,
    credentials: 'same-origin'
  }).then(async data => {
    // A 504 is a Gateway Timeout Error
    if (data.status === 200) {
      roleGroupsListFromSource = await data.json();
      console.log('roleGroupsListFromSource', roleGroupsListFromSource);
    } else {
      roleGroupsListFromSource = [];
    }
  }).catch(error => console.error(`${error}`));
  return roleGroupsListFromSource;
};